import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { marked } from "marked";
import deepseek from "../images/illustrations/deepseek.webp";
import chatgpt from "../images/illustrations/chatgpt.webp";

// Build a new allowlisted tree: model HTML never enters the page directly.
function renderAnswer(markdown) {
  const document = new DOMParser().parseFromString(marked.parse(markdown), "text/html");
  const allowed = new Set([
    "P",
    "BR",
    "STRONG",
    "EM",
    "UL",
    "OL",
    "LI",
    "BLOCKQUOTE",
    "PRE",
    "CODE",
    "H1",
    "H2",
    "H3",
    "H4",
    "HR",
    "TABLE",
    "THEAD",
    "TBODY",
    "TR",
    "TH",
    "TD",
    "A",
    "DEL",
  ]);
  const blocked = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "SVG", "MATH", "TEMPLATE"]);
  const clean = (node) => {
    if (node.nodeType === 3) return document.createTextNode(node.textContent);
    if (node.nodeType !== 1 || blocked.has(node.tagName)) return document.createTextNode("");
    const element = allowed.has(node.tagName)
      ? document.createElement(node.tagName.toLowerCase())
      : document.createDocumentFragment();
    if (node.tagName === "A") {
      const href = (node.getAttribute("href") || "").trim();
      if (/^(https?:\/\/|mailto:|\/(?!\/)|#)/i.test(href)) {
        element.setAttribute("href", href);
        if (/^https?:/i.test(href)) {
          element.setAttribute("target", "_blank");
          element.setAttribute("rel", "noopener noreferrer");
        }
      }
    }
    node.childNodes.forEach((child) => element.appendChild(clean(child)));
    return element;
  };
  const result = document.createElement("div");
  document.body.childNodes.forEach((node) => result.appendChild(clean(node)));
  return result.innerHTML;
}
const prompts = [
  ["研究方向", "你目前的研究方向是什么？"],
  ["代表论文", "请介绍一下郭博士的代表论文"],
  ["合作联系", "如何联系郭博士开展研究合作？"],
];

export default function Chat({ cvUrl, email }) {
  const [input, setInput] = useState("");
  const [answer, setAnswer] = useState(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(true);
  const inputRef = useRef(null);
  const requestRef = useRef(null);
  const lastQuestion = useRef("");

  useEffect(() => {
    const selectProject = () => {
      if (!window.location.hash.startsWith("#ask?")) return;
      try {
        setInput(decodeURIComponent(window.location.hash.slice(5)));
        window.document
          .getElementById("ask")
          .scrollIntoView({ behavior: "smooth", block: "center" });
        inputRef.current?.focus({ preventScroll: true });
      } catch (_) {
        /* Ignore malformed external hashes. */
      }
    };
    selectProject();
    window.addEventListener("hashchange", selectProject);
    return () => {
      window.removeEventListener("hashchange", selectProject);
      requestRef.current?.abort();
    };
  }, []);

  async function ask(question) {
    const prompt = question.trim();
    if (!prompt || requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    lastQuestion.current = prompt;
    setInput(prompt);
    setPending(true);
    setError("");
    setExpanded(true);
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("request failed");
      const data = await response.json();
      const content =
        data?.choices?.[0]?.message?.content ||
        data?.choices?.[0]?.delta?.content ||
        data?.content ||
        data?.reply;
      if (typeof content !== "string" || !content.trim()) throw new Error("empty response");
      setAnswer({ question: prompt, html: renderAnswer(content) });
    } catch (reason) {
      setError(
        reason.name === "AbortError"
          ? "回答暂时还没到，请稍后重试。也可以直接通过邮件联系我。"
          : "AI 助手暂时无法回答，请重试，或直接查看下方的研究与论文。"
      );
    } finally {
      clearTimeout(timer);
      requestRef.current = null;
      setPending(false);
    }
  }

  return (
    <section id="ask" className="ask-section" aria-labelledby="ask-title">
      <img
        className="mascot mascot-left"
        src={deepseek}
        alt=""
        aria-hidden="true"
        width="240"
        height="300"
        loading="lazy"
      />
      <img
        className="mascot mascot-right"
        src={chatgpt}
        alt=""
        aria-hidden="true"
        width="240"
        height="300"
        loading="lazy"
      />
      <div className="ask-panel">
        <p className="ask-label">
          <span aria-hidden="true">✧</span> AI 助手
        </p>
        <h2 id="ask-title">关于我的研究，你想了解什么？</h2>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            ask(input);
          }}
        >
          <label className="sr-only" htmlFor="research-question">
            输入你想了解的问题
          </label>
          <div className="ask-input-row">
            <textarea
              ref={inputRef}
              id="research-question"
              rows="1"
              maxLength="2000"
              value={input}
              placeholder="例如：你如何将 AI 用于合成生物学？"
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  !event.shiftKey &&
                  !event.nativeEvent.isComposing &&
                  event.keyCode !== 229
                ) {
                  event.preventDefault();
                  ask(input);
                }
              }}
              aria-describedby="ask-disclaimer"
            />
            <button
              className="ask-submit"
              type="submit"
              disabled={pending || !input.trim()}
              aria-label={pending ? "正在获取回答" : "提交问题"}
            >
              <span aria-hidden="true">{pending ? <span className="spinner" /> : "→"}</span>
            </button>
          </div>
        </form>
        <div className="suggested-prompts" aria-label="试试这些问题">
          {prompts.map(([label, question]) => (
            <button type="button" key={label} onClick={() => ask(question)} disabled={pending}>
              {label} <span aria-hidden="true">↗</span>
            </button>
          ))}
        </div>
        <p id="ask-disclaimer" className="ask-disclaimer">
          一个问题，一点了解。AI 回答仅供参考，研究信息请以论文与简历为准。
        </p>
        <div className="sr-only" role="status" aria-live="polite">
          {pending ? "正在整理回答" : error ? "回答失败，可以重试" : answer ? "回答已更新" : ""}
        </div>
        {(answer || pending || error) && (
          <div className="answer-area">
            <div className="answer-heading">
              <p className="ask-label">
                <span aria-hidden="true">✧</span> {pending ? "正在整理回答" : "AI 回答"}
              </p>
              <button
                type="button"
                className="answer-toggle"
                aria-expanded={expanded}
                aria-controls="research-answer"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? "收起 −" : "展开 +"}
              </button>
            </div>
            <div id="research-answer" hidden={!expanded} aria-busy={pending}>
              {pending && (
                <div className="loading-note">
                  <span className="loading-dots" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </span>
                  <span>
                    {answer
                      ? "正在准备新的回答，上一条内容暂时保留。"
                      : "正在查阅研究信息，请稍候…"}
                  </span>
                </div>
              )}
              {error && (
                <div className="answer-error" role="alert">
                  <p>{error}</p>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => ask(lastQuestion.current)}
                  >
                    重新尝试 ↗
                  </button>
                </div>
              )}
              {answer && (
                <div className="answer-content">
                  <p className="answered-question">{answer.question}</p>
                  <div
                    className="answer-markdown"
                    dangerouslySetInnerHTML={{ __html: answer.html }}
                  />
                </div>
              )}
              {!pending && (
                <div className="answer-links">
                  <a href="#research">查看研究方向 ↗</a>
                  <a href={cvUrl} target="_blank" rel="noreferrer">
                    下载 CV ↗
                  </a>
                  <a href={`mailto:${email}`}>邮件联系 ↗</a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

Chat.propTypes = {
  cvUrl: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};
